import React, { Component, ErrorInfo, ReactNode } from 'react';
import { isChunkLoadError } from '../../../utils/lazyWithRetry';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

// ── Full-page ErrorBoundary (used in App.tsx root) ────────────────────────────
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-2xl shadow-emerald-900/10 p-10 border border-emerald-50 text-center animate-in zoom-in-95 duration-500">
            <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center mx-auto mb-8 ring-8 ring-red-50/50">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>

            <h1 className="text-2xl font-black text-[#0D1A12] mb-3 tracking-tight">Something went wrong</h1>
            <p className="text-sm text-[#64748B] font-medium leading-relaxed mb-10">
              MoniFlow encountered an unexpected error. Don't worry, your data is safe. Try refreshing the page.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full flex items-center justify-center gap-3 bg-[#0B6E4F] hover:bg-[#065F46] text-white font-black py-4 px-6 rounded-2xl transition-all shadow-xl shadow-emerald-900/10 active:scale-95 group"
              >
                <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
                Refresh Page
              </button>

              <button
                onClick={() => (window.location.href = '/seller')}
                className="w-full flex items-center justify-center gap-3 bg-white border border-gray-100 text-[#0D1A12] font-bold py-4 px-6 rounded-2xl hover:bg-gray-50 transition-all active:scale-95"
              >
                <Home className="w-5 h-5 text-[#9CA3AF]" />
                Back to Dashboard
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <div className="mt-8 pt-8 border-t border-gray-50">
                <p className="text-[10px] font-mono text-red-400 text-left bg-red-50 p-4 rounded-xl overflow-auto max-h-32">
                  {this.state.error?.toString()}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ── Route-level ErrorBoundary ─────────────────────────────────────────────────
// Wraps individual lazy-loaded route pages. Resets automatically when the
// `resetKey` prop changes (i.e. on navigation to a new route), so a crash on
// one page never permanently breaks the shell layout.

interface RouteProps {
  children: ReactNode;
  /** Pass the current pathname so the boundary auto-resets on navigation. */
  resetKey: string;
}

interface RouteState {
  hasError: boolean;
  error: Error | null;
  errorKey: string;
}

export class RouteErrorBoundary extends Component<RouteProps, RouteState> {
  public state: RouteState = {
    hasError: false,
    error: null,
    errorKey: '',
  };

  public static getDerivedStateFromError(error: Error): Partial<RouteState> {
    return { hasError: true, error };
  }

  // Reset the boundary whenever the route changes.
  public static getDerivedStateFromProps(
    props: RouteProps,
    state: RouteState,
  ): Partial<RouteState> | null {
    if (state.hasError && props.resetKey !== state.errorKey) {
      return { hasError: false, error: null, errorKey: props.resetKey };
    }
    if (!state.hasError) {
      return { errorKey: props.resetKey };
    }
    return null;
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[RouteErrorBoundary] Caught error:', error, errorInfo);

    // Auto-reload once for chunk-load failures (stale Vite HMR URL or
    // post-deployment stale bundle).
    // Uses a timestamp guard: allows a fresh reload if >10s has passed
    // since the last one, preventing infinite reload loops.
    if (isChunkLoadError(error)) {
      const reloadKey = `chunk_reload_${window.location.pathname}`;
      const lastReload = Number(sessionStorage.getItem(reloadKey) ?? 0);
      const now = Date.now();

      if (now - lastReload > 10_000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
      }
    }
  }

  public render() {
    if (this.state.hasError) {
      const isChunk = isChunkLoadError(this.state.error);

      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="max-w-sm w-full bg-white rounded-3xl shadow-xl shadow-red-900/5 p-8 border border-red-50 text-center">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 ${isChunk ? 'bg-amber-50' : 'bg-red-50'}`}>
              {isChunk
                ? <RefreshCw className="w-7 h-7 text-amber-500 animate-spin" />
                : <AlertTriangle className="w-7 h-7 text-red-500" />
              }
            </div>

            <h2 className="text-lg font-black text-[#0D1A12] mb-2">
              {isChunk ? 'Refreshing page…' : 'Page failed to load'}
            </h2>
            <p className="text-xs text-[#64748B] font-medium leading-relaxed mb-6">
              {isChunk
                ? 'A newer version of MoniFlow is available. Refreshing to load it…'
                : 'Something went wrong on this page. Your data is safe — navigate to another page or refresh.'
              }
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 flex items-center justify-center gap-2 bg-[#0B6E4F] hover:bg-[#065F46] text-white font-bold py-3 px-4 rounded-xl transition-all active:scale-95 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={() => (window.location.href = '/seller')}
                className="flex-1 flex items-center justify-center gap-2 bg-gray-50 border border-gray-100 text-[#374151] font-bold py-3 px-4 rounded-xl hover:bg-gray-100 transition-all active:scale-95 text-sm"
              >
                <Home className="w-4 h-4" />
                Home
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <p className="mt-4 text-[9px] font-mono text-red-400 text-left bg-red-50 p-3 rounded-lg overflow-auto max-h-24">
                {this.state.error?.toString()}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
